import memoize from 'lodash/memoize'

import { createSelector } from '@app/utils/createSelector'
import { normalize } from '@app/utils/normalizer'
import { catchFetchDataError } from '@app/utils/performFetchData'

import { isApiError } from '@app/packages/api/errors'
import type { ApiGiftCard, ApiGiftCardType } from '@app/packages/api/v2/types/entities'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'

import { routingDateSelector, routingUUIDSelector } from './routing'

const giftcardTypesSlice = createReduxSlice<SliceState<{ [key: string]: ApiGiftCardType }>>('giftcard_types')
const giftcardDetailsSlice = createReduxSlice<SliceState<ApiGiftCard>>('giftcard_details')

const giftcardTypesObjectResultSelector = createSelector([giftcardTypesSlice.selector, routingDateSelector], (sliceState, date) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: giftcardTypesSlice,
      date: { date, expiresInMinutes: 30 },
      load: async dispatch => {
        const { getGiftCardsTypes } = await import('@app/packages/api/v2/actions/gift_cards')
        const resp = await dispatch(callV2Api(getGiftCardsTypes())).catch(catchFetchDataError(dispatch, 'Giftcard types fetch failed'))
        return normalize(resp).gift_card_types ?? {}
      },
    })
  )
)

export const giftcardTypesResultSelector = mapThunkSelector(giftcardTypesObjectResultSelector, types => Object.values(types))

export const getGiftcardDetailsResultSelector = memoize((guuid: string) => {
  const detailsResultSelector = createSelector([giftcardDetailsSlice.selector, routingUUIDSelector], (sliceState, uuid) =>
    createLoadable(c =>
      withCheckedSliceState(c, {
        sliceState,
        slice: giftcardDetailsSlice,
        hash: [guuid, uuid].join(':'),
        load: async dispatch => {
          const { getGiftCardsById } = await import('@app/packages/api/v2/actions/gift_cards')
          const resp = await dispatch(callV2Api(getGiftCardsById(guuid))).catch(
            catchFetchDataError(dispatch, 'Giftcard fetch failed', e => ({ handled: isApiError(e) && (e.status === 404 || e.status === 403) }))
          )
          return resp.data
        },
      })
    )
  )

  return createSelector([detailsResultSelector, giftcardTypesObjectResultSelector], (detailsResult, typesResult) =>
    createLoadable(c => {
      const giftcard = c.unwrap(detailsResult)
      const types = c.unwrap(typesResult)
      const type = types[giftcard.relationships.gift_card_type.data!.id]
      if (!type) throw new Error('Giftcard type is missing')
      return { giftcard, type }
    })
  )
})
